import { useEffect } from 'react';
import { positionStringToNumber } from '@/helpers/players';
import type { UiDuel } from '@/hooks/data/duels';
import type { PlayerDto } from '@/types/players';

/**
 * Hook для гидрации stub игроков реальными данными из загруженных дуэлей
 *
 * Когда игрок восстанавливается из URL по ID, создается stub объект без данных.
 * Этот hook находит реальные данные игрока в загруженных дуэлях и обновляет состояние.
 */
export function useDuelsPlayerHydration(
  duels: UiDuel[],
  selectedPlayerA: PlayerDto | null,
  selectedPlayerB: PlayerDto | null,
  setSelectedPlayerA: (p: PlayerDto | null) => void,
  setSelectedPlayerB: (p: PlayerDto | null) => void,
) {
  useEffect(() => {
    if (!duels || duels.length === 0) return;

    // Helper для гидрации одного игрока
    const hydratePlayer = (
      selected: PlayerDto | null,
      setPlayer: (p: PlayerDto | null) => void,
    ) => {
      // Пропускаем если игрок не выбран или уже имеет реальные данные
      if (!selected?.id || selected.web_name) return;

      // Ищем игрока в загруженных дуэлях
      const realPlayer = duels.find(d =>
        d.playerAId === selected.id || d.playerBId === selected.id,
      );

      if (realPlayer) {
        const playerData = realPlayer.playerAId === selected.id
          ? realPlayer.player1
          : realPlayer.player2;

        setPlayer({
          id: selected.id,
          fpl_id: 0, // Not available in UiDuelPlayer
          web_name: playerData.name,
          element_type: positionStringToNumber(playerData.position),
          team: playerData.team,
        } as PlayerDto);
      }
    };

    // Гидрируем оба игрока
    hydratePlayer(selectedPlayerA, setSelectedPlayerA);
    hydratePlayer(selectedPlayerB, setSelectedPlayerB);
    // Используем только id и web_name в dependencies чтобы избежать лишних reruns
    // при изменении других свойств объектов (team, element_type и т.д.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    duels,
    selectedPlayerA?.id,
    selectedPlayerA?.web_name,
    selectedPlayerB?.id,
    selectedPlayerB?.web_name,
    setSelectedPlayerA,
    setSelectedPlayerB,
  ]);
}
